/**
 * RoleHolders.jsx — who actually holds the role you are looking at.
 *
 * The Roles tab defines what a role can do; until now you had to leave it and go
 * to Users & Security to find out who has it, and come back to compare. The two
 * halves of the same question lived on different screens.
 *
 * Entra remains the source of truth for WHO holds a role — this list reads the
 * live assignments and writes through the same endpoint the Users & access table
 * uses, so a change here is the same change made there.
 *
 * "+ Add" assigns the role from here too, so checking whether a role fits and
 * giving it to someone is one step. Someone who already holds a DIFFERENT role
 * directly is MOVED (PATCH), not given a second one — two direct roles would
 * leave auth.js picking the higher one silently.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAccessUsers, changeAccessRole, addAccessUser } from '../api/client.js';
import StaffEmailInput from './StaffEmailInput.jsx';

export default function RoleHolders({ roleValue, roleLabel }) {
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [busyId, setBusyId]   = useState(null);
  const [filter, setFilter]   = useState('');
  const [adding, setAdding]   = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [notice, setNotice]   = useState(null);

  const load = useCallback(() => {
    setError(null);
    getAccessUsers()
      .then(setData)
      .catch(e => setError(e.message || 'Could not load role holders.'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const roles = data?.roles || [];
  const holders = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (data?.users || [])
      .filter(u => u.role === roleValue && u.principalType !== 'Group')
      .filter(u => !q || (u.email || '').toLowerCase().includes(q) || (u.displayName || '').toLowerCase().includes(q))
      .sort((a, b) => (a.email || a.displayName || '').localeCompare(b.email || b.displayName || ''));
  }, [data, roleValue, filter]);

  const groups = useMemo(
    () => (data?.users || []).filter(u => u.role === roleValue && u.principalType === 'Group'),
    [data, roleValue],
  );

  async function move(u, nextRole) {
    if (!nextRole || nextRole === u.role) return;
    const who = u.email || u.displayName;
    if (!window.confirm(`Move ${who} from ${roleLabel} to ${nextRole}?\n\nThey take the new role's permissions on next sign-in.`)) return;
    setBusyId(u.assignmentId); setError(null);
    try {
      await changeAccessRole({ assignmentId: u.assignmentId, principalId: u.principalId, role: nextRole });
      load();
    } catch (e) { setError(e.message || 'Could not change the role.'); }
    finally { setBusyId(null); }
  }

  // Close the add form when switching to another role.
  useEffect(() => { setAdding(false); setNewEmail(''); setNotice(null); }, [roleValue]);

  async function add(e) {
    e?.preventDefault?.();
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) { setError('Enter the person’s email.'); return; }
    setError(null); setNotice(null);
    const direct = (data?.users || []).filter(u => u.principalType !== 'Group' && (u.email || '').toLowerCase() === email);
    if (direct.some(u => u.role === roleValue)) { setNotice(`${email} already holds ${roleLabel}.`); return; }
    const current = direct[0];
    if (current) {
      const from = roles.find(r => r.value === current.role)?.label || current.role;
      if (!window.confirm(`${email} currently holds ${from}.\n\nMove them to ${roleLabel}? They take the new role's permissions on next sign-in.`)) return;
    }
    setAddBusy(true);
    try {
      if (current) {
        await changeAccessRole({ assignmentId: current.assignmentId, principalId: current.principalId, role: roleValue });
        setNotice(`Moved ${email} to ${roleLabel}.`);
      } else {
        const r = await addAccessUser({ email, role: roleValue });
        setNotice(r?.already ? `${email} already holds ${roleLabel}.`
          : `Added ${r?.email || email} to ${roleLabel}${r?.emailed ? ' — welcome email sent.' : '.'}`);
      }
      setNewEmail(''); setAdding(false);
      load();
    } catch (e2) { setError(e2.message || 'Could not add them to this role.'); }
    finally { setAddBusy(false); }
  }

  if (!roleValue) {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
        This role has no Entra value yet, so nobody can hold it.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <div className="admin-section-title" style={{ fontSize: 14 }}>Who holds this role</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn sm" onClick={() => { setAdding(a => !a); setError(null); setNotice(null); }}
            disabled={!data} style={{ fontSize: 11 }} aria-expanded={adding}>
            {adding ? 'Cancel' : '+ Add'}
          </button>
          <button className="btn ghost sm" onClick={load} style={{ fontSize: 11 }}>Refresh</button>
        </div>
      </div>
      <div className="admin-section-desc" style={{ marginBottom: 10 }}>
        {data
          ? `${holders.length} ${holders.length === 1 ? 'person' : 'people'} hold ${roleLabel} directly.`
          : 'Loading…'}
      </div>

      {holders.length > 6 && (
        <input className="input" value={filter} onChange={e => setFilter(e.target.value)}
          placeholder="Filter by name or email…" style={{ fontSize: 12, marginBottom: 8, width: '100%' }} />
      )}

      {adding && (
        <form onSubmit={add} style={{
          display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10,
          padding: '8px 9px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)',
        }}>
          <StaffEmailInput
            value={newEmail} autoFocus directory
            annotate={e => {
              const cur = (data?.users || []).find(u => u.principalType !== 'Group' && (u.email || '').toLowerCase() === e);
              if (!cur) return null;
              return cur.role === roleValue ? `Already holds ${roleLabel}`
                : `Holds ${roles.find(r => r.value === cur.role)?.label || cur.role} — will be moved`;
            }}
            onChange={v => { setNewEmail(v); setError(null); }}
            ariaLabel={`Email of the person to add to ${roleLabel}`}
            inputClass="input sm" selectClass="select"
            style={{ flex: '1 1 220px', minWidth: 0 }}
            inputStyle={{ fontSize: 12, padding: '4px 8px' }}
            selectStyle={{ fontSize: 12 }}
          />
          <button className="btn sm" type="submit" disabled={addBusy || !newEmail.trim()} style={{ fontSize: 11 }}>
            {addBusy ? 'Adding…' : `Add to ${roleLabel}`}
          </button>
        </form>
      )}

      {error && <div className="field-error" style={{ marginBottom: 8 }}>{error}</div>}
      {notice && <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 8 }}>{notice}</div>}

      {data && holders.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '10px 0' }}>
          Nobody holds this role yet — use + Add to assign it.
        </div>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
        {holders.map(u => (
          <li key={u.assignmentId} style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)',
          }}>
            <div style={{ minWidth: 0, flex: '1 1 180px' }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={u.email || ''}>{u.email || '—'}</div>
              {u.displayName && (
                <div style={{ fontSize: 11, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.displayName}
                </div>
              )}
            </div>
            <select className="select" value={u.role || ''} disabled={busyId === u.assignmentId}
              onChange={e => move(u, e.target.value)}
              aria-label={`Role for ${u.email || u.displayName}`}
              style={{ fontSize: 11.5, flex: '0 0 auto', maxWidth: 170 }}>
              {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </li>
        ))}
      </ul>

      {groups.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-3)', lineHeight: 1.6 }}>
          Also held through {groups.length === 1 ? 'a group' : `${groups.length} groups`}:{' '}
          {groups.map(g => g.displayName).filter(Boolean).join(', ')}. Group membership is managed in Entra.
        </div>
      )}
    </div>
  );
}
